'use client';

import React, { use, useState, useEffect, useMemo } from 'react';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import Link from 'next/link';
import SvgRightCaret from '@/components/Icons/SvgRightCaret';
import { HeaderUIStateContext } from '@/components/header/header_menu/HeaderUIStateProvider';
import { getLinkTargetData } from '@/utils/getLinkTargetData';
import Cookies from 'js-cookie';

export default function MobileNav({ data, children }) {
  const pathname = useAppPathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedSubNav, setSelectedSubNav] = useState(undefined);
  const [maxWidth, setMaxWidth] = useState('max-w-[260px]');
  const isAuthenticated = Boolean(Cookies.get('custaccesstoken'));
  const memoizedChildren = useMemo(
    () => React.Children.toArray(children),
    [children]
  );

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.documentElement.style.overflow = mobileMenuOpen
      ? 'hidden'
      : 'visible';
  }, [mobileMenuOpen]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 640) {
        setMaxWidth('max-w-[260px]');
      } else if (window.innerWidth < 1024) {
        setMaxWidth('max-w-[620px]');
      } else {
        setMaxWidth('max-w-[260px]');
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleMobileMenu = () => {
    setMobileMenuOpen((prevState) => !prevState);
  };

  const handleToggleMobileSubMenu = (_, index) => {
    setSelectedSubNav((prev) => (prev === index ? null : index));
  };

  return (
    <nav
      className={`2lg:hidden order-1 w-1/3 ${maxWidth} text-sm`}
      aria-label="Mobile Navigation"
      role="navigation"
      data-cy="mobile-nav"
    >
      <div
        className="flex max-w-[24px] cursor-pointer justify-center"
        onClick={handleToggleMobileMenu}
      >
        <button
          type="button"
          className="flex h-[60px] items-center px-1"
          data-cy="mobile-hamburger"
          aria-expanded={mobileMenuOpen}
          aria-controls="menu-drawer"
        >
          {mobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
          <span className="sr-only">
            {mobileMenuOpen ? 'Close menu' : 'Open menu'}
          </span>
        </button>
      </div>
      <div
        id="menu-drawer"
        className={`${mobileMenuOpen ? 'translate-x-0' : 'invisible -translate-x-full'} ease absolute top-[60px] left-0 z-40 h-screen w-full max-w-[360px] overflow-hidden bg-white transition-transform duration-400`}
        aria-hidden={!mobileMenuOpen}
        role="menu"
      >
        <div className="h-full w-full overflow-y-auto pb-[220px]">
          <ul className="2lg:mb-0 mt-2 mb-[160px] block w-full list-none bg-white">
            {data.map((item, index) => {
              const hasSubnav = item.has_subnav;
              let linkText, linkUrl, linkTitle;
              if (!hasSubnav) {
                const linkData =
                  getLinkTargetData({
                    label: item.label,
                    label_link: item.label_link,
                    external_link: item.external_link,
                    title: item.title
                  }) || {};
                linkText = linkData.linkText;
                linkUrl = linkData.linkUrl;
                linkTitle = linkData.linkTitle;
              }

              const labelColorClass = !hasSubnav
                ? pathname === linkUrl
                  ? 'text-cozy-cognac'
                  : item.label_text_color
                : item.label_text_color;

              return (
                <MenuItem
                  key={`mobile-mainNav-navItem-${index}`}
                  item={item}
                  index={index}
                  pathname={pathname}
                  data={data}
                  linkData={{ url: linkUrl, title: linkTitle, text: linkText }}
                  labelColorClass={labelColorClass}
                  handleToggleMobileSubMenu={handleToggleMobileSubMenu}
                  memoizedChildren={memoizedChildren}
                  mobileMenuOpen={mobileMenuOpen}
                  selectedSubNav={selectedSubNav}
                  isAuthenticated={isAuthenticated}
                />
              );
            })}
          </ul>
        </div>
      </div>
      <div
        onClick={() => setMobileMenuOpen(false)}
        className={`ease transition-opacity duration-400 ${mobileMenuOpen ? 'bg-dawn-grey/60 pointer-events-auto block' : 'bg-dawn-grey/0 pointer-events-none hidden'} absolute top-[60px] z-30 h-screen w-screen`}
      />
    </nav>
  );
}

const MenuItem = ({
  item,
  index,
  linkData,
  data,
  labelColorClass,
  handleToggleMobileSubMenu,
  memoizedChildren,
  mobileMenuOpen,
  selectedSubNav,
  isAuthenticated
}) => {
  const isAccountLink = item.label_link?.[0]?.url?.startsWith('/account');
  const accountRedirect = isAuthenticated ? '/account' : '/account/auth/login';
  const { label } = item;
  const { isDesktop } = use(HeaderUIStateContext);
  const dataHeap = `mainNav-navItem-${label.replaceAll(' ', '-').toLowerCase()}`;
  const externalHref = item.external_link?.href;
  const internalHref = item.label_link?.[0]?.url;
  const isPhoneLink = item.link_type === 'Phone' || /^\d/.test(externalHref);

  const baseHref = externalHref || internalHref || '#';
  const finalHref = isPhoneLink ? `tel:${externalHref}` : baseHref;

  const dataCy = isAccountLink
    ? 'mobile-header-account-link'
    : 'mobile-header-support-link';

  function show(item) {
    return !isDesktop &&
      (item.item_display !== 'desktop only' ||
        item.item_display.includes('mobile'))
      ? true
      : false;
  }

  /**
   * KNOWLEDGE WARNING
   *
   * Currently, menu items that have a subnav should NOT define `link_type`.
   * All subnav "parent" items are expected to have `link_type` undefined.
   *
   * When a menu item *does* define a `link_type`, and it either:
   *  - appears after a group of subnav items, OR
   *  - is the first item in the menu
   *
   * then extra spacing + a top border are applied to visually separate it
   * from the preceding subnav group.
   *
   * This means:
   *  - Subnav groups must come first
   *  - All subnav parents must leave `link_type` empty
   *  - Only standalone / direct-link items should define `link_type`
   *
   * If this convention is broken, spacing and visual grouping will break.
   */
  return show(item) ? (
    item.has_subnav ? (
      <li
        data-heap={dataHeap}
        className={`order-${index} aria-expanded:text-cozy-cognac w-full px-9`}
        aria-hidden={!mobileMenuOpen}
        aria-label={linkData?.text ?? label}
        data-cy="mobile-nav-item"
      >
        <button
          type="button"
          className="flex w-full cursor-pointer items-center py-4 text-left"
          onClick={(_) => handleToggleMobileSubMenu(_, index)}
          aria-expanded={selectedSubNav === index}
        >
          <span className={`h5 pr-[10px] ${labelColorClass}`}>
            {linkData?.text ?? label}
          </span>
          <SvgRightCaret aria-hidden="true" />
          <span className="sr-only">
            {selectedSubNav === index ? 'Collapse' : 'Expand'}{' '}
            {linkData?.text ?? label} submenu
          </span>
        </button>
        <div
          className={`fixed top-0 left-0 z-50! h-full w-[360px] bg-white transition duration-400 ${selectedSubNav === index ? 'translate-x-0' : 'translate-x-[360px]'}`}
        >
          <div className="h-full overflow-y-auto pb-[220px]">
            <button
              type="button"
              className="fixed top-0 z-10 flex w-full cursor-pointer border-b bg-white p-5 text-left"
              onClick={handleToggleMobileSubMenu}
              aria-label={`Back to main menu from ${linkData?.text ?? label} submenu`}
            >
              <ArrowSvg />
              <div className="h5 pl-[10px]">{linkData?.text ?? label}</div>
              <span className="sr-only">Back to main menu</span>
            </button>
            {React.cloneElement(memoizedChildren[index])}
          </div>
        </div>
      </li>
    ) : item.mobile_navigation_placement === 'secondary' ? (
      (() => {
        const prevPlacement = (
          data[index - 1]?.mobile_navigation_placement ?? ''
        )
          .toString()
          .toLowerCase()
          .trim();

        const needsTopBorder = index === 0 || prevPlacement !== 'secondary';

        const href = isAccountLink ? accountRedirect : finalHref;

        const content = (
          <div className="flex items-center justify-start">
            <div
              className="mask mr-2 h-5 w-5 bg-black mask-center mask-no-repeat whitespace-nowrap"
              style={{ maskImage: `url(${item?.icon?.url})` }}
            />
            <span className={`body-l ${labelColorClass}`}>{item.label}</span>
          </div>
        );

        return (
          <li
            className={`header-mobile-item w-full cursor-pointer px-9 ${
              needsTopBorder ? 'border-dawn-grey-20 mt-2 border-t' : ''
            }`}
          >
            <div className={`${needsTopBorder ? 'pt-[43px]' : 'pt-4'} pb-4`}>
              {isAccountLink && !isAuthenticated ? (
                <a href={href} data-cy={dataCy}>
                  {content}
                </a>
              ) : (
                <Link href={href} prefetch={false} data-cy={dataCy}>
                  {content}
                </Link>
              )}
            </div>
          </li>
        );
      })()
    ) : (
      <li
        className={`order-${index} w-full cursor-pointer px-9`}
        aria-label={linkData?.text ?? label}
        data-cy="mobile-nav-item"
      >
        {isAccountLink && !isAuthenticated ? (
          <a
            data-heap={dataHeap}
            href={accountRedirect}
            title={linkData?.title}
            data-cy={dataCy}
          >
            <div className="flex items-center justify-between py-4">
              <span className={`h5 ${labelColorClass}`}>
                {linkData?.text ?? label}
              </span>
            </div>
          </a>
        ) : (
          <Link
            data-heap={dataHeap}
            href={
              isAccountLink
                ? accountRedirect
                : `${linkData?.url || '/'}${item.label_query_string ?? ''}`
            }
            prefetch={false}
            title={linkData?.title}
            data-cy={dataCy}
          >
            <div className="flex items-center justify-between py-4">
              <span className={`h5 ${labelColorClass}`}>
                {linkData?.text ?? label}
              </span>
            </div>
          </Link>
        )}
      </li>
    )
  ) : null;
};

function CloseIcon() {
  return (
    <span
      className="bg-nav-close h-[12px] w-[14px] bg-no-repeat"
      aria-hidden="true"
    />
  );
}

function HamburgerIcon() {
  return (
    <span
      className="bg-nav-hamburger h-[12px] w-[14px] bg-no-repeat"
      aria-hidden="true"
    />
  );
}

// ArrowSvg component for the back navigation arrow
function ArrowSvg({ ...props }) {
  return (
    <svg
      className="rotate-180 self-center"
      width="24px"
      height="24px"
      viewBox="0 0 24 24"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      {...props}
    >
      <title>Back arrow</title>
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g
          stroke="#0E3739"
          strokeDasharray="0,0"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <g transform="translate(3.000000, 7.000000)">
            <line x1="0" y1="5" x2="18" y2="5"></line>
            <path d="M13,0 L18,5 L13,10"></path>
          </g>
        </g>
      </g>
    </svg>
  );
}
